import React, { useEffect, useRef, useState } from "react";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

const MEDIA_SECTION_TYPES = new Set(["hero", "slider", "gallery", "video", "downloads", "custom"]);

export const sectionSupportsMedia = (sectionType) =>
  MEDIA_SECTION_TYPES.has((sectionType || "").toLowerCase());

const previewSrc = (item, localPreview) => {
  if (localPreview) return localPreview;
  if (!item?.url) return "";
  const base = resolveMediaUrl(item.url);
  const version = item.updatedAtUtc || item.rowVersion || "";
  return version ? `${base}${base.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}` : base;
};

const MediaPreview = ({ item, localPreview, className = "h-48 w-full rounded-xl object-cover" }) => {
  const url = previewSrc(item, localPreview);
  if (!url) {
    return (
      <div className="flex h-48 w-full items-center justify-center rounded-xl bg-slate-100 text-sm text-slate-400">
        No image
      </div>
    );
  }

  if ((item?.mediaType || "").toLowerCase() === "video" && !localPreview) {
    return (
      <video controls className={className} src={url} key={url}>
        <track kind="captions" />
      </video>
    );
  }

  return (
    <img
      key={url}
      src={url}
      alt={item?.altText || item?.originalFileName || "Media"}
      className={className}
    />
  );
};

const MediaSlotCard = ({ item, busy, selected, onSelect, onReplace, onDelete }) => {
  const inputRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);

  useEffect(() => {
    setLocalPreview(null);
  }, [item.url, item.updatedAtUtc, item.rowVersion]);

  useEffect(() => {
    if (!busy) {
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [busy]);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const handleFile = (file) => {
    if (!file) return;
    if (localPreview) URL.revokeObjectURL(localPreview);
    const previewUrl = URL.createObjectURL(file);
    setLocalPreview(previewUrl);
    onReplace(item, file);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(item.id);
        }
      }}
      className={[
        "rounded-2xl border bg-white p-3 shadow-sm transition",
        selected
          ? "border-emerald-500 ring-2 ring-emerald-200"
          : "border-slate-200 hover:border-slate-300",
      ].join(" ")}
    >
      <MediaPreview item={item} localPreview={localPreview} />

      <div className="mt-3 space-y-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-800">
            {item.groupKey || "image"}
          </p>
          <p className="truncate text-xs text-slate-500">
            {item.originalFileName || item.url}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/mp4,video/webm"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              handleFile(file);
            }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item.id);
              inputRef.current?.click();
            }}
            className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
          >
            {busy && selected ? "Replacing…" : "Replace image"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            className="rounded-xl border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 disabled:opacity-60"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

const AddMediaBox = ({ onUpload, busy }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [groupKey, setGroupKey] = useState("main");

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleFile = (file) => {
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    onUpload({ file, groupKey });
  };

  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
      <p className="mb-3 text-sm font-semibold text-slate-800">Add image</p>
      {preview ? (
        <img src={preview} alt="Selected upload" className="mb-3 h-40 w-full rounded-xl object-cover" />
      ) : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="block text-sm sm:w-40">
          <span className="mb-1 block text-slate-600">Slot / group</span>
          <input
            value={groupKey}
            onChange={(e) => setGroupKey(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2"
            placeholder="banner"
          />
        </label>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/mp4,video/webm"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            handleFile(file);
          }}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? "Uploading…" : "Select image"}
        </button>
      </div>
    </div>
  );
};

const MediaManager = ({ mediaItems, onUpload, onReplace, onDelete, busy }) => {
  const [selectedId, setSelectedId] = useState(mediaItems[0]?.id ?? null);

  useEffect(() => {
    if (!mediaItems.some((m) => m.id === selectedId)) {
      setSelectedId(mediaItems[0]?.id ?? null);
    }
  }, [mediaItems, selectedId]);

  return (
    <div className="space-y-4">
      {mediaItems.length === 0 ? (
        <AddMediaBox onUpload={onUpload} busy={busy} />
      ) : (
        <>
          <p className="text-sm text-slate-500">
            Select an image, then replace it. The preview updates as soon as you choose a file.
          </p>
          <div className="grid gap-4 sm:grid-cols-1">
            {mediaItems.map((item) => (
              <MediaSlotCard
                key={item.id}
                item={item}
                busy={busy}
                selected={selectedId === item.id}
                onSelect={setSelectedId}
                onReplace={onReplace}
                onDelete={onDelete}
              />
            ))}
          </div>
          <AddMediaBox onUpload={onUpload} busy={busy} />
        </>
      )}
    </div>
  );
};

export default MediaManager;
export { MediaPreview };
